import type React from 'react'

/* ── Styles ───────────────────────────────────────────────────── */
export const s: Record<string, React.CSSProperties> = {
  root: { minHeight: '100vh' },

  modeEmoji: { fontSize: '1.8rem', lineHeight: 1 },
  modeLabel: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: '1.15rem', fontWeight: 700, color: '#1B4332', textAlign: 'center' as const,
  },
  modeDesc: { fontSize: '0.875rem', color: '#9A8070', fontWeight: 500, textAlign: 'center' as const, lineHeight: 1.4 },

  // ── Play shared ──
  playRoot: { minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#FAF6EF' },

  playMain: {
    flex: 1, maxWidth: 860, width: '100%', margin: '0 auto',
    padding: '24px 20px 36px',
    display: 'flex', flexDirection: 'column', gap: 18,
  },

  // ── Drawer ──
  drawerFrame: {
    background: 'linear-gradient(180deg, #7A4F2D 0%, #6B4226 40%, #5A3520 100%)',
    borderRadius: 16, padding: 10,
    boxShadow: '0 8px 32px rgba(107,66,38,0.35), inset 0 1px 0 rgba(255,255,255,0.15), inset 0 -2px 6px rgba(0,0,0,0.3)',
  },
  drawerInner: { display: 'flex', gap: 8 },

  // ── Compartment ──
  compartment: {
    flex: 1, borderRadius: 10, padding: '12px 10px 10px',
    display: 'flex', flexDirection: 'column',
    minHeight: 160, gap: 8,
  },
  compartmentPieces:   { flex: 1, display: 'flex', flexWrap: 'wrap', gap: 4, alignContent: 'flex-start', justifyContent: 'center', minHeight: 80 },
  compartmentPiece:    { background: 'rgba(255,255,255,0.55)', borderRadius: 5, padding: '4px 3px', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'opacity 0.2s ease' },
  compartmentOverflow: { fontSize: '0.875rem', fontWeight: 800, alignSelf: 'center', padding: '4px 6px', background: 'rgba(255,255,255,0.6)', borderRadius: 8 },
  compartmentFooter:   { display: 'flex', justifyContent: 'center', alignItems: 'center', paddingTop: 6, borderTop: '1px solid rgba(0,0,0,0.07)' },
  compartmentLabel:    { fontSize: '0.875rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.07em', textAlign: 'center' },

  // ── Table mode ──
  tableMain: {
    flex: 1, maxWidth: 900, width: '100%', margin: '0 auto',
    padding: '24px 16px 36px',
    display: 'flex', flexDirection: 'column', gap: 20,
  },
  tableCloth: {
    background: 'linear-gradient(135deg, #C8A96E 0%, #B8955A 50%, #A07840 100%)',
    borderRadius: 20,
    padding: '20px 16px',
    boxShadow: '0 6px 28px rgba(107,66,38,0.3), inset 0 1px 0 rgba(255,255,255,0.2)',
    // Subtle linen texture via repeating gradient
    backgroundImage: `
      repeating-linear-gradient(
        45deg,
        rgba(255,255,255,0.03) 0px, rgba(255,255,255,0.03) 1px,
        transparent 1px, transparent 8px
      ),
      linear-gradient(135deg, #C8A96E 0%, #B8955A 50%, #A07840 100%)
    `,
  },
  tableCovers: {
    display: 'flex', flexWrap: 'wrap', gap: 20, justifyContent: 'center',
  },

  // ── Place mat ──
  placemat: {
    width: 264,
    background: '#F5F0E8',
    borderRadius: 16,
    overflow: 'hidden',
    transition: 'border-color 0.15s ease, box-shadow 0.2s ease',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
  },

  // Always-visible dish list at top of placemat
  coverDishList: {
    padding: '12px 14px 10px',
    borderBottom: '1px solid rgba(0,0,0,0.08)',
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    background: 'rgba(255,255,255,0.45)',
  },
  coverDishRow: {
    display: 'flex', alignItems: 'center', gap: 8,
    overflow: 'hidden',
  },
  coverDishEmoji: { fontSize: '1.5rem', lineHeight: 1, flexShrink: 0 },
  coverDishName: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: '1.4rem', fontWeight: 600, color: '#2D1810',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },

  // Plate area — relative container for cutlery layout
  plateArea: {
    position: 'relative',
    height: 170,
    flexShrink: 0,
  },

  // Placed cutlery positions (absolute within plateArea 264×170)
  // Plate center: x=132, y=88  (60px circle → left:102, top:58)
  plate: {
    position: 'absolute',
    width: 60, height: 60,
    top: 58, left: 102,
    borderRadius: '50%',
    background: 'radial-gradient(circle at 35% 35%, #FFFFFF 0%, #F0EDE8 100%)',
    border: '2px solid #DDD5C8',
    boxShadow: '0 2px 6px rgba(0,0,0,0.12), inset 0 1px 3px rgba(255,255,255,0.8)',
  },
  // Teaspoon: centered above plate
  placedTeaspoon: {
    position: 'absolute',
    top: 18, left: '50%', transform: 'translateX(-50%)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1,
  },
  // Fork: left of plate. center vertically with plate center y=88
  placedFork: {
    position: 'absolute',
    top: 51, left: 36,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  // Knife: right of plate
  placedKnife: {
    position: 'absolute',
    top: 51, left: 168,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  // Spoon: right of knife
  placedSpoon: {
    position: 'absolute',
    top: 51, left: 196,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },

  completeBadge: {
    position: 'absolute', bottom: 8, right: 10,
    width: 24, height: 24, borderRadius: '50%',
    background: '#52B788', color: '#FFFFFF',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '0.875rem', fontWeight: 800,
  },

  // ── Menu overlay ──
  menuOverlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 100,
    padding: '24px 20px',
  },
  menuCard: {
    background: '#FFFEF8',
    border: '1.5px solid #D4C4A0',
    borderRadius: 20,
    padding: '28px 32px',
    maxWidth: 340, width: '100%',
    boxShadow: '0 24px 64px rgba(0,0,0,0.35)',
    // Subtle ruled-paper lines
    backgroundImage: `repeating-linear-gradient(
      transparent,
      transparent 31px,
      #E8DFC8 31px,
      #E8DFC8 32px
    )`,
    backgroundPositionY: '48px',
  },
  menuCardHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: 20,
    paddingBottom: 12,
    borderBottom: '2px solid #D4C4A0',
  },
  menuCardTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: '1.3rem', fontWeight: 700, color: '#1B4332',
  },
  menuCloseBtn: {
    fontSize: '0.875rem', fontWeight: 700, color: '#9A8070',
    background: '#F0E8D8', border: '1px solid #D8C9B8',
    borderRadius: 8, padding: '6px 12px', cursor: 'pointer', minHeight: 36,
  },
  menuDishes: { display: 'flex', flexDirection: 'column', gap: 16 },
  menuDish: {
    display: 'flex', alignItems: 'center', gap: 14,
    padding: '8px 0',
  },
  menuDishEmoji: { fontSize: '2rem', lineHeight: 1, flexShrink: 0 },
  menuDishName: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: '1.2rem', fontWeight: 600, color: '#2D1810', lineHeight: 1.3,
  },

  // ── Instruction ──
  instruction: {
    textAlign: 'center', fontSize: '1rem',
    color: '#6B5040', fontWeight: 600, fontStyle: 'italic',
    padding: '4px 0',
  },

  // ── Pile ──
  pile: {
    display: 'flex', flexWrap: 'wrap',
    gap: 10, justifyContent: 'center',
    padding: '12px 8px',
    background: '#FFFFFF', borderRadius: 16, border: '1.5px solid #EFE7D7',
    boxShadow: '0 2px 12px rgba(27,67,50,0.06)',
    minHeight: 100,
  },

  // ── Pile card ──
  pileCard: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: 64, height: 96,
    background: '#FAFAFA', border: '1.5px solid #E8E0D8',
    borderRadius: 12, cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(27,67,50,0.08)',
    transition: 'transform 0.18s ease, box-shadow 0.18s ease, border-color 0.15s ease, opacity 0.15s ease',
    padding: '10px 8px',
  },
  pileCardSelected: {
    border: '2px solid #D4A017',
    boxShadow: '0 8px 24px rgba(212,160,23,0.3), 0 2px 8px rgba(27,67,50,0.1)',
    background: '#FFFBF0',
  },

  // ── Complete ──
  completeRoot: {
    minHeight: '100vh',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'linear-gradient(155deg, #6B4226 0%, #8B5A3A 40%, #A07040 100%)',
    padding: '32px 20px', position: 'relative', overflow: 'hidden',
  },
  completeBg1: { position: 'absolute', top: -80, right: -80, width: 360, height: 360, borderRadius: '50%', background: 'rgba(255,255,255,0.05)', pointerEvents: 'none' },
  completeBg2: { position: 'absolute', bottom: -60, left: -60, width: 280, height: 280, borderRadius: '50%', background: 'rgba(27,67,50,0.15)', pointerEvents: 'none' },
  completeCard: {
    position: 'relative', zIndex: 1,
    background: '#FFFFFF', borderRadius: 24,
    padding: '44px 36px', maxWidth: 420, width: '100%',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20,
    boxShadow: '0 24px 80px rgba(0,0,0,0.3)', textAlign: 'center',
  },
  completeDrawer: {
    display: 'flex', gap: 6, padding: 8,
    background: '#6B4226', borderRadius: 12,
    boxShadow: '0 4px 16px rgba(107,66,38,0.3)',
    animation: 'matchPop 0.4s ease-out',
  },
  completeMiniSlot: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
    padding: '8px 6px', borderRadius: 6, border: '1.5px solid',
  },
  completeTitle:     { fontFamily: "'Cormorant Garamond', serif", fontSize: '2.6rem', fontWeight: 700, color: '#1B4332', lineHeight: 1.05 },
  completeSubtitle:  { fontSize: '1rem', color: '#6B5040', fontWeight: 500 },
  completeStats: {
    display: 'flex', alignItems: 'center', gap: 20,
    background: '#FAF6EF', borderRadius: 14, padding: '18px 24px',
    width: '100%', justifyContent: 'center', border: '1.5px solid #EFE7D7',
  },
  completeStat:        { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 },
  completeStatVal:     { fontFamily: "'Cormorant Garamond', serif", fontSize: '2rem', fontWeight: 700, color: '#1B4332', lineHeight: 1 },
  completeStatLbl:     { fontSize: '0.875rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9A8070' },
  completeStatDivider: { width: 1.5, height: 36, background: '#E3D5C0' },
  completeBtns:        { display: 'flex', flexDirection: 'column', gap: 10, width: '100%', marginTop: 4 },
  completeBtnPrimary: {
    fontFamily: 'Nunito, sans-serif',
    fontSize: '1.05rem', fontWeight: 800, color: '#FFFFFF',
    background: 'linear-gradient(135deg, #6B4226, #8B5A3A)',
    border: 'none', borderRadius: 12, padding: '15px 24px',
    cursor: 'pointer', minHeight: 52, letterSpacing: '0.02em',
  },
  completeBtnSecondary: {
    fontFamily: 'Nunito, sans-serif',
    fontSize: '1rem', fontWeight: 700, color: '#6B4226',
    background: '#EFE7D7', border: '1.5px solid #D8C9B8',
    borderRadius: 12, padding: '13px 24px', cursor: 'pointer', minHeight: 48,
  },
  completeBtnGhost: {
    fontFamily: 'Nunito, sans-serif',
    fontSize: '0.9rem', fontWeight: 700, color: '#9A8070',
    background: 'transparent', border: 'none',
    cursor: 'pointer', padding: '8px', minHeight: 40,
  },

  // ── Count mode ──
  countMain: {
    flex: 1, maxWidth: 600, width: '100%', margin: '0 auto',
    padding: '24px 20px 48px',
    display: 'flex', flexDirection: 'column', gap: 20,
  },
  comandaCard: {
    background: '#FFFFFF', border: '1.5px solid #EFE7D7',
    borderRadius: 16, padding: '20px 20px 16px',
    boxShadow: '0 4px 20px rgba(27,67,50,0.07)',
    display: 'flex', flexDirection: 'column', gap: 10,
  },
  comandaTitleRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    paddingBottom: 8, borderBottom: '1px solid #EFE7D7',
  },
  comandaTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: '1rem', fontWeight: 700, color: '#9A8070',
    textTransform: 'uppercase' as const, letterSpacing: '0.07em',
  },
  comandaDiners: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: '1.65rem', fontWeight: 700, color: '#1B4332',
  },
  comandaRow: {
    display: 'flex', alignItems: 'center', gap: 12,
  },
  comandaQty: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: '1.95rem', fontWeight: 700, color: '#1B4332',
    minWidth: 44, textAlign: 'right' as const,
  },
  comandaEmoji: { fontSize: '2.1rem', lineHeight: 1 },
  comandaName: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: '1.75rem', fontWeight: 600, color: '#2E1F14',
  },

  extractedGrid: {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10,
  },
  extractedSlot: {
    borderRadius: 12, border: '1.5px solid',
    padding: '12px 12px 10px',
    display: 'flex', flexDirection: 'column', gap: 8,
    minHeight: 80, transition: 'border-color 0.2s, background 0.2s',
  },
  extractedSlotHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  },
  extractedSlotLabel: {
    fontSize: '0.875rem', fontWeight: 800,
    textTransform: 'uppercase' as const, letterSpacing: '0.07em',
  },
  extractedCount: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: '1.3rem', fontWeight: 700, lineHeight: 1,
  },
  extractedPieces: {
    display: 'flex', flexWrap: 'wrap' as const, gap: 4, minHeight: 28,
  },
  extractedPiece: {
    background: 'rgba(255,255,255,0.7)', border: '1px solid rgba(0,0,0,0.1)',
    borderRadius: 6, padding: '4px 5px',
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    minWidth: 32, minHeight: 32,
    transition: 'background 0.12s',
  },
  extractedEmpty: {
    fontSize: '1rem', color: '#C0A882', alignSelf: 'center',
  },

  countInstruction: {
    fontSize: '0.875rem', color: '#9A8070', fontWeight: 600,
    textAlign: 'center' as const, margin: 0,
  },

  countDrawerFrame: {
    background: 'linear-gradient(180deg, #7A4F2D 0%, #6B4226 40%, #5A3520 100%)',
    borderRadius: 14, padding: 8,
    boxShadow: '0 6px 24px rgba(107,66,38,0.3), inset 0 1px 0 rgba(255,255,255,0.12)',
  },
  countDrawerSlot: {
    flex: 1, borderRadius: 8,
    border: '1.5px solid',
    padding: '12px 8px 10px',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
    cursor: 'pointer', minHeight: 80,
    transition: 'filter 0.12s',
  },
  countDrawerLabel: {
    fontSize: '0.875rem', fontWeight: 800,
    textTransform: 'uppercase' as const, letterSpacing: '0.06em',
    textAlign: 'center' as const,
  },
  countDrawerPlus: {
    fontSize: '1.1rem', fontWeight: 900, lineHeight: 1,
  },

  checkBtn: {
    fontFamily: 'Nunito, sans-serif',
    fontSize: '1.05rem', fontWeight: 800, color: '#FFFFFF',
    background: 'linear-gradient(135deg, #1B4332, #2D6A4F)',
    border: 'none', borderRadius: 12,
    padding: '15px 32px', cursor: 'pointer',
    minHeight: 52, letterSpacing: '0.02em',
    boxShadow: '0 4px 18px rgba(27,67,50,0.25)',
    alignSelf: 'center' as const,
  },
  countFeedback: {
    fontSize: '0.95rem', fontWeight: 700, color: '#B5541A',
    textAlign: 'center' as const, margin: 0,
  },
}
